import React from "react";
import styled from "styled-components";

import { Button } from "@examspark/common-ui";
import { IconCheckedBoxLine } from "@examspark/common-ui/dist/components/icons";
import { Cartesian } from "@examspark/types";

type ShapeControlButtonProps = {
  mode: string | undefined;
  setMode: React.Dispatch<React.SetStateAction<string | undefined>>;
  shapes: Cartesian.Shape[];
};
export const ShapeControlButtons = ({
  mode,
  setMode,
  shapes,
}: ShapeControlButtonProps) => (
  <>
    <Button
      title="Move"
      data-cy="move"
      variant="plain"
      color={mode === undefined ? "primary" : "secondary"}
      onClick={() => setMode(undefined)}
    >
      <svg viewBox="0 0 16 16" width="16">
        <g>
          <rect x="2.08" y="6.93" width="12" height="2" />
          <path
            d="M15.74,7.78l-3.28-2.73C12.28,4.9,12,5.03,12,5.27v5.47c0,0.24,0.28,0.37,0.46,0.22l3.28-2.73
C15.88,8.1,15.88,7.9,15.74,7.78z"
          />
          <path
            d="M0.26,8.15l3.28,2.73C3.72,11.03,4,10.9,4,10.66V5.2c0-0.24-0.28-0.37-0.46-0.22L0.26,7.71C0.12,7.83,0.12,8.03,0.26,8.15z
"
          />
        </g>
        <g>
          <rect
            x="2.12"
            y="7.01"
            transform="matrix(-1.836970e-16 1 -1 -1.836970e-16 16.1263 -0.1042)"
            width="12"
            height="2"
          />
          <path
            d="M8.26,15.68l2.73-3.21c0.16-0.18,0.03-0.46-0.21-0.46H5.32c-0.24,0-0.37,0.28-0.21,0.46l2.73,3.21
C7.94,15.81,8.15,15.81,8.26,15.68z"
          />
          <path d="M7.89,0.14L5.34,3.55C5.2,3.74,5.33,4,5.56,4h5.11c0.23,0,0.36-0.26,0.23-0.45L8.34,0.14C8.23-0.01,8-0.01,7.89,0.14z" />
        </g>
      </svg>
    </Button>
    <Button
      disabled={(shapes?.length ?? 0) >= 2}
      variant="plain"
      color={mode === "line" ? "primary" : "secondary"}
      title="Line"
      data-cy="line"
      onClick={() => {
        if (mode === "line") {
          setMode(undefined);
        } else {
          setMode("line");
        }
      }}
    >
      <svg viewBox="0 0 16 16" width="16">
        <rect x="2.08" y="6.93" width="12" height="2" />
        <path
          d="M15.65,7.77l-4.21-2.81C11.25,4.83,11,4.97,11,5.19v5.62c0,0.22,0.25,0.36,0.44,0.23l4.21-2.81
C15.82,8.12,15.82,7.88,15.65,7.77z"
        />
        <path d="M0.35,8.16l4.21,2.81C4.75,11.1,5,10.96,5,10.74V5.12C5,4.9,4.75,4.76,4.56,4.89L0.35,7.7C0.18,7.81,0.18,8.05,0.35,8.16z" />
      </svg>
    </Button>

    <Button
      disabled={(shapes?.length ?? 0) >= 2}
      variant="plain"
      color={mode === "dashed-line" ? "primary" : "secondary"}
      title="Dashed Line"
      data-cy="dashed-line"
      onClick={() => {
        if (mode === "dashed-line") {
          setMode(undefined);
        } else {
          setMode("dashed-line");
        }
      }}
    >
      <svg viewBox="0 0 16 16" width="16">
        <rect x="6.08" y="6.93" width="4" height="2" />
        <path
          d="M15.65,7.77l-4.21-2.81C11.25,4.83,11,4.97,11,5.19v5.62c0,0.22,0.25,0.36,0.44,0.23l4.21-2.81
C15.82,8.12,15.82,7.88,15.65,7.77z"
        />
        <path d="M0.35,8.16l4.21,2.81C4.75,11.1,5,10.96,5,10.74V5.12C5,4.9,4.75,4.76,4.56,4.89L0.35,7.7C0.18,7.81,0.18,8.05,0.35,8.16z" />
      </svg>
    </Button>

    <Button
      disabled={(shapes?.length ?? 0) === 0}
      variant="plain"
      color={mode === "answer" ? "primary" : "secondary"}
      title="Answer"
      data-cy="answer"
      onClick={() => {
        if (mode === "answer") {
          setMode(undefined);
        } else {
          setMode("answer");
        }
      }}
    >
      <IconCheckedBoxLine width="16" />
    </Button>

    <Button
      title="Erase"
      data-cy="erase"
      variant="plain"
      color={mode === "erase" ? "primary" : "secondary"}
      onClick={() => {
        if (mode === "erase") {
          setMode(undefined);
        } else {
          setMode("erase");
        }
      }}
      icon={
        <svg viewBox="0 0 421.542 421.542" width="16">
          <path
            id="path1155"
            d="m106.584 61.396c-24.196 0-46.346 13.775-57.167 35.417l-42.667 85.375c-9 18.001-9 39.166 0 57.167l42.667 85.333c10.821 21.642 32.97 35.458 57.167 35.458h251.042c35.094 0 63.917-28.99 63.917-64.083v-170.709c0-35.094-28.823-63.958-63.917-63.958zm122.833 85.042c5.785.044 11.303 2.435 15.292 6.625l27.542 27.5 27.542-27.5c3.864-3.977 9.124-6.293 14.667-6.458 11.778-.346 21.607 8.921 21.953 20.699.175 5.937-2.134 11.678-6.37 15.842l-27.583 27.583 27.583 27.5c8.353 8.353 8.353 21.897 0 30.25s-21.897 8.353-30.25 0l-27.542-27.542-27.542 27.542c-8.353 8.353-21.897 8.353-30.25 0s-8.353-21.897 0-30.25l27.583-27.5-27.583-27.583c-8.491-8.17-8.75-21.677-.58-30.167 4.062-4.221 9.68-6.586 15.538-6.541z"
          />
        </svg>
      }
    />
  </>
);

const CreatePrompt = styled.div`
  padding: 1rem;
  color: var(--text-medium);
  background-color: var(--bg-warm-1);
  border-bottom: var(--border-light);
  // position: absolute;
  left: 0;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
`;

const CreatePromptText = styled.div`
  flex: 1;
`;
const CreatePromptButton = styled.div``;

export const CartesianModePrompt = ({
  mode,
  setMode,
}: {
  mode: string | undefined;
  setMode: React.Dispatch<React.SetStateAction<string | undefined>>;
}) => {
  if (mode === "line" || mode === "dashed-line") {
    return (
      <CreatePrompt>
        <CreatePromptText>
          Click inside the graph to create your shape.
        </CreatePromptText>
        <CreatePromptButton>
          <Button
            // size="small"
            title={"Done Adding"}
            variant="outlined"
            onClick={() => setMode(undefined)}
          >
            Done Adding
          </Button>
        </CreatePromptButton>
      </CreatePrompt>
    );
  }

  if (mode === "answer") {
    return (
      <CreatePrompt>
        <CreatePromptText>
          Click an area on the graph to mark it as your answer.
        </CreatePromptText>
        <CreatePromptButton>
          <Button
            // size="small"
            title="Done Answering"
            variant="outlined"
            onClick={() => setMode(undefined)}
          >
            Done Answering
          </Button>
        </CreatePromptButton>
      </CreatePrompt>
    );
  }
  if (mode === "erase") {
    <CreatePrompt>
      <CreatePromptText>Click a shape to erase it.</CreatePromptText>
      <CreatePromptButton>
        <Button
          // size="small"
          title="Done Erasing"
          variant="outlined"
          onClick={() => setMode(undefined)}
        >
          Done Erasing
        </Button>
      </CreatePromptButton>
    </CreatePrompt>;
  }

  return <></>;
};
